"use client";

import NextLink from "next/link";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import { localizedHref } from "@/lib/i18n";
import { useLocale } from "@/components/i18n/LocaleProvider";

type LinkProps = ComponentProps<typeof NextLink> & {
  /**
   * When false, `href` is used as-is (already locale-prefixed or external).
   * Default true — internal paths are prefixed with the active locale.
   */
  localePrefix?: boolean;
};

function textFromChildren(children: ReactNode): string {
  if (children == null || typeof children === "boolean") return "";
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children
      .map(textFromChildren)
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (typeof children === "object" && "props" in (children as object)) {
    const el = children as ReactElement<{
      children?: ReactNode;
      "aria-label"?: string;
      alt?: string;
    }>;
    if (typeof el.props["aria-label"] === "string" && el.props["aria-label"]) {
      return el.props["aria-label"];
    }
    if (typeof el.props.alt === "string" && el.props.alt) {
      return el.props.alt;
    }
    return textFromChildren(el.props.children);
  }
  return "";
}

function hrefLabel(href: LinkProps["href"]): string {
  if (typeof href === "string") return href;
  if (href && typeof href === "object" && "pathname" in href && href.pathname) {
    return String(href.pathname);
  }
  return "";
}

/**
 * Next.js Link that always exposes a `title` for SEO/a11y scanners,
 * and prefixes internal hrefs with the active locale.
 */
export function Link({
  title,
  children,
  "aria-label": ariaLabel,
  localePrefix = true,
  href,
  ...props
}: LinkProps) {
  const locale = useLocale();
  const resolvedHref =
    localePrefix && typeof href === "string"
      ? localizedHref(locale, href)
      : href;

  const resolvedTitle =
    (typeof title === "string" && title.trim()) ||
    (typeof ariaLabel === "string" && ariaLabel.trim()) ||
    textFromChildren(children) ||
    hrefLabel(resolvedHref);

  return (
    <NextLink
      title={resolvedTitle || undefined}
      aria-label={ariaLabel}
      href={resolvedHref}
      {...props}
    >
      {children}
    </NextLink>
  );
}
